import { useEffect, useState } from 'react';

import { t } from '../lib/i18n';

/** How long the stream may be down before the operator is told to reload. */
export const OFFLINE_BANNER_AFTER_MS = 30_000;

interface StreamStatusDotProps {
  /** From the shell's single `useAdminBidStream`. */
  connected: boolean;
  /** Timestamp of the current outage's start; null while connected. */
  disconnectedSince: number | null;
  /** Override for tests; production keeps the 30 s default. */
  offlineBannerAfterMs?: number;
  className?: string;
}

/**
 * Pure rule behind the banner: an outage older than the threshold, measured
 * against `nowMs`. Exported so the timing can be asserted without a clock.
 */
export function isOfflineTooLong(
  disconnectedSince: number | null,
  nowMs: number,
  thresholdMs: number = OFFLINE_BANNER_AFTER_MS,
): boolean {
  return disconnectedSince !== null && nowMs - disconnectedSince >= thresholdMs;
}

/**
 * Realtime indicator for the app shell — a green dot while the admin bid
 * stream is open, grey while EventSource is reconnecting, and after 30 s of
 * outage a banner telling the operator that bid updates are not arriving.
 *
 * The banner exists because a silently dead stream is worse than no stream:
 * the operator keeps trusting a queue that stopped moving. Thirty seconds is
 * two missed keepalives (the server sends one every 15 s) — long enough to
 * ride out a routine reconnect, short enough to matter inside a tender window.
 */
export function StreamStatusDot({
  connected,
  disconnectedSince,
  offlineBannerAfterMs = OFFLINE_BANNER_AFTER_MS,
  className = '',
}: StreamStatusDotProps) {
  const [offlineTooLong, setOfflineTooLong] = useState(() =>
    isOfflineTooLong(disconnectedSince, Date.now(), offlineBannerAfterMs),
  );

  useEffect(() => {
    if (connected || disconnectedSince === null) {
      setOfflineTooLong(false);
      return undefined;
    }
    const remaining = Math.max(0, offlineBannerAfterMs - (Date.now() - disconnectedSince));
    const timer = setTimeout(() => setOfflineTooLong(true), remaining);
    return () => clearTimeout(timer);
  }, [connected, disconnectedSince, offlineBannerAfterMs]);

  const label = connected ? t('admin.stream.connected') : t('admin.stream.reconnecting');

  return (
    <>
      <span
        role="status"
        aria-label={label}
        title={label}
        data-testid="stream-status-dot"
        data-connected={connected ? 'true' : 'false'}
        className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${
          connected ? 'bg-cf-success' : 'bg-cf-ink-subtle/50'
        } ${className}`}
      />
      {!connected && offlineTooLong && (
        <div
          role="alert"
          data-testid="stream-offline-banner"
          className="fixed inset-x-0 top-[3px] z-50 flex justify-center px-4 py-2 text-center text-[13px] font-medium text-cf-ink shadow-md"
          style={{ backgroundColor: '#fde68a' }}
        >
          {t('admin.stream.offline')}
        </div>
      )}
    </>
  );
}
